import React, { useMemo } from 'react';
import clsx from 'clsx';
import classes from './index.module.scss';
import { Chip, styled } from '@mui/material';

export interface ChipOption {
  label: any;
  value: any;
  selected?: boolean;
}

export interface ChipOptionsSelectorProps {
  options: ChipOption[];
  orientation?: 'horizontal' | 'vertical';
  onOptionClick?: (optionChanged: ChipOption, index: number) => void;
  className?: string;
}

const StyledChip = styled(Chip)(() => ({
  '&.MuiChip-root': {
    background: '#F7F8F9',
    height: '46px',
    border: 'none',
    fontFamily: 'system-ui',
    borderRadius: '27px',
    fontSize: '18px',

    paddingLeft: '20px',
    paddingRight: '20px',
  },
  '&.MuiChip-filled': {
    background: '#3784F6',
    color: '#FFF',
    '& span': {
      color: '#FFF',
    },
  },
}));

export function ChipOptionsSelector(props: ChipOptionsSelectorProps) {
  return (
    <div
      className={clsx(
        classes.root,
        props.className,
        classes[`orientation_${props.orientation || 'horizontal'}`]
      )}
    >
      {props.options.map((option, index) => (
        <StyledChip
          key={`option-${option.value}`}
          data-test-id={`option-${option.value}`}
          variant={option.selected ? 'filled' : 'outlined'}
          label={option.label}
          onClick={() => props.onOptionClick?.(option, index)}
        />
      ))}
    </div>
  );
}

export interface ChipOptionsSelectorControlledProps {
  desfaultOptions: ChipOption[];
  multiSelect?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onOptionsSelectionChange?: (
    selectedOptions: ChipOption[],
    optionChanged: ChipOption,
    index: number
  ) => void;
  className?: string;
}

export function ChipOptionsSelectorControlled(
  props: ChipOptionsSelectorControlledProps
) {
  const [options, setOptions] = React.useState<ChipOption[]>(
    props.desfaultOptions.map((option) => ({ ...option }))
  );

  const onOptionClick = (optionChanged: ChipOption, index: number) => {
    const newOptions = [...options];
    if (!props.multiSelect) {
      newOptions.forEach((option: ChipOption) => (option.selected = false));
    }
    newOptions[index].selected = !newOptions[index].selected;
    setOptions(newOptions);
    props.onOptionsSelectionChange?.(newOptions, optionChanged, index);
  };

  return (
    <ChipOptionsSelector
      onOptionClick={onOptionClick}
      options={options}
      className={props.className}
      orientation={props.orientation}
    />
  );
}

export interface YesNoOptionsSelectorProps {
  onChange: (isChecked: boolean) => void;
  isChecked?: boolean;
  className?: string;
}

export function YesNoOptionsSelector(props: YesNoOptionsSelectorProps) {
  const options = useMemo(
    () => [
      { label: 'Yes', value: 'yes', selected: props.isChecked === true },
      { label: 'No', value: 'no', selected: props.isChecked === false },
    ],
    []
  );
  return (
    <ChipOptionsSelectorControlled
      desfaultOptions={options}
      className={props.className}
      onOptionsSelectionChange={(selectedOptions) => {
        props.onChange(!!selectedOptions[0].selected);
      }}
    />
  );
}
